// Resolves which chatbot conversation belongs to a given offer.
// The rebase `offers.conversation_service_conversation_id` column is unreliable
// (often empty), so instead we match the offer against each conversation's
// `offer_ids` array (conv-svc stores rebase position_id / Offer.id there).
// One fetch per applicant, cached + in-flight deduped.

const inFlight = {}

const state = {
  // applicantId(string) -> { [stringId]: conversationId }
  byApplicant: {}
}

const mutations = {
  setMap (state, { applicantId, map }) {
    state.byApplicant = {
      ...state.byApplicant,
      [applicantId]: map
    }
  }
}

const actions = {
  async fetchForApplicant ({ commit, state }, { applicantId, vm, force }) {
    if (!applicantId) return null
    const key = String(applicantId)
    if (!force && state.byApplicant[key]) return state.byApplicant[key]
    if (inFlight[key]) return inFlight[key]

    inFlight[key] = (async () => {
      try {
        const res = await vm.$network_utils.getConvSvcConversations({
          rebase_applicant_id: applicantId,
          per_page: 100,
          // Same tenant scope every other conv-svc caller sends. Without it the
          // lookup could map an offer id to a conversation belonging to another
          // tenant's applicant with the same rebase id — conv-svc keys applicant
          // profiles on (rebase_applicant_id, operation), so 12345 is three
          // different people across rebase / suitup / piola.
          operation: vm.$settings.get({ path: 'intake_operation_slug' }) || undefined
        })
        const convs = res && Array.isArray(res.data) ? res.data.slice() : []
        // oldest -> newest, so the most recent conversation wins when an id
        // appears in more than one conversation's offer_ids.
        convs.sort((a, b) =>
          new Date(a.last_event_at || a.started_at || 0) - new Date(b.last_event_at || b.started_at || 0)
        )
        const map = {}
        convs.forEach(c => {
          const ids = Array.isArray(c.offer_ids) ? c.offer_ids : []
          ids.forEach(id => { map[String(id)] = c.id })
        })
        commit('setMap', { applicantId: key, map })
        return map
      } catch (err) {
        console.error('[offerConversations] fetch error:', err && err.message)
        commit('setMap', { applicantId: key, map: {} })
        return {}
      } finally {
        delete inFlight[key]
      }
    })()

    return inFlight[key]
  }
}

const getters = {
  // offer_ids may hold either the rebase Offer.id or the position_id — match both.
  getConvId: (state) => (applicantId, offerId, positionId) => {
    const map = state.byApplicant[String(applicantId)]
    if (!map) return null
    return map[String(offerId)] || map[String(positionId)] || null
  },
  isLoaded: (state) => (applicantId) => {
    return !!state.byApplicant[String(applicantId)]
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
